import { lazy, Suspense } from 'react';

import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner';
import { AuthGuard } from '@/shared/components/guards/AuthGuard';
import { PublicGuard } from '@/shared/components/guards/PublicGuard';
import { DashboardLayout } from '@/shared/components/layout/DashboardLayout';
import { LegalConsentModal } from '@/shared/components/LegalConsentModal';
import { TrialBanner } from '@/shared/components/TrialBanner';

const SignInPage = lazy(() =>
  import('@/features/auth/pages/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const SignUpPage = lazy(() =>
  import('@/features/auth/pages/SignUpPage').then((m) => ({ default: m.SignUpPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/pages/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const PasswordResetPage = lazy(() =>
  import('@/features/auth/pages/PasswordResetPage').then((m) => ({
    default: m.PasswordResetPage,
  })),
);
const OnboardingPage = lazy(() =>
  import('@/features/auth/pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const ProfileSelectPage = lazy(() =>
  import('@/features/auth/pages/ProfileSelectPage').then((m) => ({
    default: m.ProfileSelectPage,
  })),
);

const AppointmentsPage = lazy(() =>
  import('@/features/appointments/pages/AppointmentsPage').then((m) => ({
    default: m.AppointmentsPage,
  })),
);
const PatientsPage = lazy(() =>
  import('@/features/patients/pages/PatientsPage').then((m) => ({
    default: m.PatientsPage,
  })),
);
const TemplatesPage = lazy(() =>
  import('@/features/templates/pages/TemplatesPage').then((m) => ({
    default: m.TemplatesPage,
  })),
);
const ReferralsPage = lazy(() =>
  import('@/features/referrals/pages/ReferralsPage').then((m) => ({
    default: m.ReferralsPage,
  })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/pages/SettingsPage').then((m) => ({
    default: m.SettingsPage,
  })),
);
const ProfilesPage = lazy(() =>
  import('@/features/profiles/pages/ProfilesPage').then((m) => ({
    default: m.ProfilesPage,
  })),
);
const PatientDetailPage = lazy(() =>
  import('@/features/patients/pages/PatientDetailPage').then((m) => ({
    default: m.PatientDetailPage,
  })),
);
const ChartDetailPage = lazy(() =>
  import('@/features/charts/pages/ChartDetailPage').then((m) => ({
    default: m.ChartDetailPage,
  })),
);

function SmartRedirect() {
  return (
    <PublicGuard>
      <Navigate to="/auth/signin" replace />
    </PublicGuard>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <TrialBanner />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/auth/signin"
            element={
              <PublicGuard>
                <SignInPage />
              </PublicGuard>
            }
          />
          <Route
            path="/auth/signup"
            element={
              <PublicGuard>
                <SignUpPage />
              </PublicGuard>
            }
          />
          <Route
            path="/auth/password-forgot"
            element={
              <PublicGuard>
                <ForgotPasswordPage />
              </PublicGuard>
            }
          />
          <Route
            path="/auth/password-reset"
            element={
              <PublicGuard>
                <PasswordResetPage />
              </PublicGuard>
            }
          />
          <Route
            path="/authAction"
            element={
              <PublicGuard>
                <PasswordResetPage />
              </PublicGuard>
            }
          />

          {/* Auth-protected routes (non-dashboard) */}
          <Route
            path="/dashboard/additional-info"
            element={
              <AuthGuard>
                <OnboardingPage />
              </AuthGuard>
            }
          />
          <Route
            path="/profiles/select"
            element={
              <AuthGuard>
                <ProfileSelectPage />
              </AuthGuard>
            }
          />

          {/* Dashboard routes with sidebar layout */}
          <Route
            path="/dashboard"
            element={
              <AuthGuard>
                <DashboardLayout />
              </AuthGuard>
            }
          >
            <Route index element={<Navigate to="appointments" replace />} />
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="patients" element={<PatientsPage />} />
            <Route path="patients/:patientId" element={<PatientDetailPage />} />
            <Route path="charts/:chartId" element={<ChartDetailPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="referrals" element={<ReferralsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="profiles" element={<ProfilesPage />} />
          </Route>

          {/* Preview routes — no auth required, for design review */}
          <Route path="/preview" element={<DashboardLayout />}>
            <Route index element={<Navigate to="appointments" replace />} />
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="patients" element={<PatientsPage />} />
            <Route path="patients/:patientId" element={<PatientDetailPage />} />
            <Route path="charts/:chartId" element={<ChartDetailPage />} />
            <Route path="profiles" element={<ProfilesPage />} />
          </Route>

          {/* Redirects */}
          <Route path="/getStartedToday" element={<Navigate to="/auth/signup" replace />} />
          <Route path="/" element={<SmartRedirect />} />
          <Route path="*" element={<SmartRedirect />} />
        </Routes>
      </Suspense>
      <LegalConsentModal />
    </BrowserRouter>
  );
}
